import { DocumentText, DocumentPrevious, DocumentUpload, TickCircle, Box, InfoCircle, Edit2, Profile, TableDocument, Message2, MessageText, ClipboardText, TaskSquare, Camera, Trash } from "iconsax-react";
import type { Icon as IconType } from "iconsax-react";
import type { BadgeColor } from "@/shared/ui/Badge";
import type { ActionType, ActionRequired } from "../types/pending.types";

export type PendingCategory = "salary" | "contract" | "application" | "order" | "request" | "act" | "task" | "report";

export const PENDING_CATEGORY_MAP: Record<
  PendingCategory,
  {
    labelKey: string;
    icon: IconType;
    badgeColor: BadgeColor;
  }
> = {
  salary: {
    labelKey: "categories.salary",
    icon: Profile,
    badgeColor: "info",
  },
  contract: {
    labelKey: "categories.contract",
    icon: TableDocument,
    badgeColor: "gray",
  },
  application: {
    labelKey: "categories.application",
    icon: DocumentPrevious,
    badgeColor: "notice",
  },
  order: {
    labelKey: "categories.order",
    icon: DocumentText,
    badgeColor: "notice",
  },
  request: {
    labelKey: "categories.request",
    icon: Message2,
    badgeColor: "info",
  },
  act: {
    labelKey: "categories.act",
    icon: MessageText,
    badgeColor: "gray",
  },
  task: {
    labelKey: "categories.task",
    icon: TaskSquare,
    badgeColor: "info",
  },
  report: {
    labelKey: "categories.report",
    icon: Camera,
    badgeColor: "notice",
  },
} as const;

export function getItemCategory(originalType: ActionType): PendingCategory {
  const mapping = ACTION_TYPE_MAP[originalType];
  if (!mapping) return "application";
  return mapping.category;
}

export const ACTION_REQUIRED_MAP: Record<ActionRequired, { labelKey: string; icon: React.ReactNode; color: string }> = {
  sign_document: {
    labelKey: "actionRequired.sign_document",
    icon: <DocumentText size={16} color="currentColor" />,
    color: "notice",
  },
  approve_project_removal: {
    labelKey: "actionRequired.approve_project_removal",
    icon: <Trash size={16} color="currentColor" />,
    color: "negative",
  },
  upload_signed_order: {
    labelKey: "actionRequired.upload_signed_order",
    icon: <DocumentUpload size={16} color="currentColor" />,
    color: "info",
  },
  approve_application: {
    labelKey: "actionRequired.approve_application",
    icon: <TickCircle size={16} color="currentColor" />,
    color: "positive",
  },
  acknowledge_order: {
    labelKey: "actionRequired.acknowledge_order",
    icon: <ClipboardText size={16} color="currentColor" />,
    color: "gray",
  },
  approve_draft: {
    labelKey: "actionRequired.approve_draft",
    icon: <TickCircle size={16} color="currentColor" />,
    color: "positive",
  },
  approve_timesheet: {
    labelKey: "actionRequired.approve_timesheet",
    icon: <TickCircle size={16} color="currentColor" />,
    color: "positive",
  },
  approve_request: {
    labelKey: "actionRequired.approve_request",
    icon: <TickCircle size={16} color="currentColor" />,
    color: "positive",
  },
  fulfill_request: {
    labelKey: "actionRequired.fulfill_request",
    icon: <Box size={16} color="currentColor" />,
    color: "info",
  },
  issue_business_trip: {
    labelKey: "actionRequired.issue_business_trip",
    icon: <DocumentUpload size={16} color="currentColor" />,
    color: "notice",
  },
  settle_business_trip: {
    labelKey: "actionRequired.settle_business_trip",
    icon: <ClipboardText size={16} color="currentColor" />,
    color: "notice",
  },
  issue_advance: {
    labelKey: "actionRequired.issue_advance",
    icon: <DocumentUpload size={16} color="currentColor" />,
    color: "notice",
  },
  settle_advance: {
    labelKey: "actionRequired.settle_advance",
    icon: <ClipboardText size={16} color="currentColor" />,
    color: "notice",
  },
  unsettled_salary_advance: {
    labelKey: "actionRequired.unsettled_salary_advance",
    icon: <InfoCircle size={16} color="currentColor" />,
    color: "notice",
  },
  submit_for_signing: {
    labelKey: "actionRequired.submit_for_signing",
    icon: <DocumentText size={16} color="currentColor" />,
    color: "notice",
  },
  wait: {
    labelKey: "actionRequired.wait",
    icon: <InfoCircle size={16} color="currentColor" />,
    color: "gray",
  },
  revise_application: {
    labelKey: "actionRequired.revise_application",
    icon: <Edit2 size={16} color="currentColor" />,
    color: "notice",
  },
  review_gph_payment: {
    labelKey: "actionRequired.review_gph_payment",
    icon: <TickCircle size={16} color="currentColor" />,
    color: "positive",
  },
  complete_task: {
    labelKey: "actionRequired.complete_task",
    icon: <TaskSquare size={16} color="currentColor" />,
    color: "notice",
  },
  review_fsm_submission: {
    labelKey: "actionRequired.review_fsm_submission",
    icon: <Camera size={16} color="currentColor" />,
    color: "positive",
  },
  approve_data_change: {
    labelKey: "actionRequired.approve_data_change",
    icon: <TickCircle size={16} color="currentColor" />,
    color: "positive",
  },
  approve_data_change_chief: {
    labelKey: "actionRequired.approve_data_change_chief",
    icon: <TickCircle size={16} color="currentColor" />,
    color: "positive",
  },
  fill_personal_sheet: {
    labelKey: "actionRequired.fill_personal_sheet",
    icon: <Profile size={16} color="currentColor" />,
    color: "notice",
  },
  sign_personal_sheet: {
    labelKey: "actionRequired.sign_personal_sheet",
    icon: <Edit2 size={16} color="currentColor" />,
    color: "notice",
  },
  close_medical_leave: {
    labelKey: "actionRequired.close_medical_leave",
    icon: <DocumentUpload size={16} color="currentColor" />,
    color: "notice",
  },
} as const;

export const ACTION_TYPE_MAP: Record<ActionType, { labelKey: string; category: PendingCategory }> = {
  vacation_order: { labelKey: "types.vacation_order", category: "order" },
  medical_order: { labelKey: "types.medical_order", category: "order" },
  termination_order: { labelKey: "types.termination_order", category: "order" },
  amendment_order: { labelKey: "types.amendment_order", category: "order" },
  hiring_order: { labelKey: "types.hiring_order", category: "order" },
  leave_application: { labelKey: "types.leave_application", category: "application" },
  amendment: { labelKey: "types.amendment", category: "application" },
  annual_leave: { labelKey: "types.annual_leave", category: "application" },
  unpaid_leave: { labelKey: "types.unpaid_leave", category: "application" },
  medical_leave: { labelKey: "types.medical_leave", category: "application" },
  resignation_letter: { labelKey: "types.resignation_letter", category: "application" },
  amendment_application: { labelKey: "types.amendment_application", category: "application" },
  amendment_agreement: { labelKey: "types.amendment_agreement", category: "contract" },
  contract_draft: { labelKey: "types.contract_draft", category: "contract" },
  job_application: { labelKey: "types.job_application", category: "application" },
  service_agreement_individual: { labelKey: "types.service_agreement_individual", category: "contract" },
  service_agreement_msb: { labelKey: "types.service_agreement_msb", category: "contract" },
  vehicle_rental: { labelKey: "types.vehicle_rental", category: "contract" },
  premises_lease: { labelKey: "types.premises_lease", category: "contract" },
  goods_supply: { labelKey: "types.goods_supply", category: "contract" },
  completion_act: { labelKey: "types.completion_act", category: "act" },
  vehicle_handover: { labelKey: "types.vehicle_handover", category: "act" },
  premises_handover: { labelKey: "types.premises_handover", category: "act" },
  timesheet_draft: { labelKey: "types.timesheet_draft", category: "salary" },
  unit_request: { labelKey: "types.unit_request", category: "request" },
  unit_request_fulfill: { labelKey: "types.unit_request_fulfill", category: "request" },
  gph_payment: { labelKey: "types.gph_payment", category: "salary" },
  task_deadline_approaching: { labelKey: "types.task_deadline_approaching", category: "task" },
  fsm_submission: { labelKey: "types.fsm_submission", category: "report" },
  data_change_request: { labelKey: "types.data_change_request", category: "request" },
  personal_sheet_completion: { labelKey: "types.personal_sheet_completion", category: "application" },
  personal_sheet_signature: { labelKey: "types.personal_sheet_signature", category: "application" },
  advance: { labelKey: "types.advance", category: "application" },
  business_trip: { labelKey: "types.business_trip", category: "application" },
  business_trip_order: { labelKey: "types.business_trip_order", category: "order" },
  project_removal_request: { labelKey: "types.project_removal_request", category: "request" },
} as const;

export const PENDING_CATEGORIES: PendingCategory[] = ["salary", "contract", "application", "order", "request", "act", "task", "report"];
